import React, { createContext, useContext, useEffect, ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LANGUAGE, Language, localizedPath, stripLanguage } from '../constants/routes';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  resetLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Chrome around the pages — navigation, footer, controls. Page copy lives in
 * the content modules; only strings that belong to the shell go here.
 *
 * Exported for `t` and for one other reader: the `nav.*` entries are what a
 * menu entry falls back to when an editor empties its label at /admin, and
 * tests need the same fallback to know what the menu says — see
 * `navLabel` in shared/constants/content.testing.ts.
 */
export const UI_STRINGS: Record<string, Record<Language, string>> = {
  'language.en': { en: 'English', de: 'Englisch' },
  'language.de': { en: 'German', de: 'Deutsch' },
  'language.switch': { en: 'Change language', de: 'Sprache wechseln' },

  'nav.home': { en: 'Home', de: 'Startseite' },
  'nav.organisation': { en: 'Organisation', de: 'Organisation' },
  'nav.mitmachen': { en: 'Join us', de: 'Mach mit' },
  'nav.instagram': { en: 'Instagram', de: 'Instagram' },
  'nav.podcast': { en: 'Podcast', de: 'Podcast' },
  'nav.menu': { en: 'Open menu', de: 'Menü öffnen' },
  'nav.close': { en: 'Close menu', de: 'Menü schließen' },
  'nav.skip': { en: 'Skip to main content', de: 'Zum Hauptinhalt springen' },

  // The footer carries three legal links and nothing else. `legalTitle` is
  // what names that list for a screen reader, since it no longer has a heading
  // anyone can see.
  'footer.impressum': { en: 'Legal notice', de: 'Impressum' },
  'footer.privacy': { en: 'Privacy', de: 'Datenschutz' },
  'footer.cookieSettings': { en: 'Cookie settings', de: 'Cookie-Einstellungen' },
  'footer.legalTitle': { en: 'Legal', de: 'Rechtliches' },
  'footer.rights': { en: 'All rights reserved.', de: 'Alle Rechte vorbehalten.' },

  'notFound.title': { en: 'This page does not exist', de: 'Diese Seite gibt es nicht' },
  'notFound.body': {
    en: 'The address may have a typo in it, or the page has been taken down. Here is where you can go instead:',
    de: 'Vielleicht hat sich in der Adresse ein Tippfehler eingeschlichen, oder die Seite wurde abgeschaltet. Hier geht es weiter:',
  },
  'notFound.home': { en: 'Back to the start page', de: 'Zurück zur Startseite' },

  'theme.toLight': { en: 'Switch to light mode', de: 'Zur hellen Ansicht wechseln' },
  'theme.toDark': { en: 'Switch to dark mode', de: 'Zur dunklen Ansicht wechseln' },

  // The tile grid's fold-out control. Chrome rather than copy: an editor writes
  // what is in a tile, never the word on the control that opens it - which has
  // to say the same thing on every tile of every page for the grid to be
  // readable at all.
  'topics.open': { en: 'Read more', de: 'Mehr lesen' },
  'topics.close': { en: 'Show less', de: 'Weniger anzeigen' },
  // The compact shelf's panel closes with a cross rather than a worded
  // control, so this is the name a screen reader reads out and nothing else.
  'topics.closePanel': { en: 'Close', de: 'Schließen' },
};

interface LanguageProviderProps {
  children: ReactNode;
}

const readStoredLanguage = (): Language | null => {
  try {
    const saved = localStorage.getItem('language');
    return saved === 'de' || saved === 'en' ? saved : null;
  } catch (error) {
    return null;
  }
};

const storeLanguage = (lang: Language | null) => {
  try {
    // Only persist if the user has accepted cookies
    if (localStorage.getItem('cookieConsent') !== 'accepted') {
      return;
    }
    if (lang === null) {
      localStorage.removeItem('language');
    } else {
      localStorage.setItem('language', lang);
    }
  } catch (error) {
    // Storage unavailable - the URL stays the source of truth
  }
};

/**
 * The URL decides the language, never the browser: /organisation is German,
 * /en/organisation is English, and both stay that way for every visitor and
 * every crawler.
 *
 * The single exception is the bare domain. Someone who explicitly picked a
 * language earlier is sent to it, so that typing the bare domain does not throw
 * them back into the language they just left. Visitors without a stored choice
 * - including crawlers, which have none - are never redirected.
 */

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // The URL is the source of truth: no prefix means the default language.
  const { language: urlLanguage, path } = stripLanguage(location.pathname);
  const language = urlLanguage ?? DEFAULT_LANGUAGE;

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Only an explicit prefix counts as a choice worth remembering - storing the
  // derived default here would overwrite a choice the visitor already made.
  useEffect(() => {
    if (urlLanguage) {
      storeLanguage(urlLanguage);
    }
  }, [urlLanguage]);

  // Only the bare domain honours a previously chosen language.
  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }
    const stored = readStoredLanguage();
    if (stored && stored !== DEFAULT_LANGUAGE) {
      navigate(`${localizedPath('/', stored)}${location.search}${location.hash}`, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  const setLanguage = useCallback(
    (lang: Language) => {
      storeLanguage(lang);
      navigate(`${localizedPath(path, lang)}${location.search}${location.hash}`);
    },
    [navigate, path, location.search, location.hash]
  );

  const resetLanguage = useCallback(() => {
    storeLanguage(null);
    navigate(`${localizedPath(path, DEFAULT_LANGUAGE)}${location.search}${location.hash}`);
  }, [navigate, path, location.search, location.hash]);

  const t = useCallback((key: string): string => UI_STRINGS[key]?.[language] ?? key, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, resetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
